import { useEffect, useSyncExternalStore } from "react"

export type Appearance = "light" | "dark" | "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  if (document.documentElement.dataset.brandedTheme) return

  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const savedAppearance = (): Appearance =>
  (localStorage.getItem("appearance") as Appearance | null) ?? "system"

const serverAppearance = (): Appearance => "system"

export const restoreAppearance = () => {
  applyTheme(savedAppearance())
}

export function initializeTheme() {
  restoreAppearance()

  mediaQuery()?.addEventListener("change", restoreAppearance)
}

const appearanceListeners = new Set<() => void>()

const subscribeToAppearance = (listener: () => void) => {
  appearanceListeners.add(listener)
  return () => {
    appearanceListeners.delete(listener)
  }
}

const updateAppearance = (mode: Appearance) => {
  if (mode === "system") {
    localStorage.removeItem("appearance")
  } else {
    localStorage.setItem("appearance", mode)
  }
  applyTheme(mode)
  appearanceListeners.forEach((listener) => listener())
}

export function useAppearance() {
  const appearance = useSyncExternalStore(
    subscribeToAppearance,
    savedAppearance,
    serverAppearance,
  )

  useEffect(() => {
    restoreAppearance()

    return () => mediaQuery()?.removeEventListener("change", restoreAppearance)
  }, [])

  return { appearance, updateAppearance } as const
}
